import { ChangeEvent, useMemo, useState } from "react";
import {
  INPUT_SIZES,
  SearchInput,
  Divider,
  TextButton,
  rdsSetNavMenuHighlight,
} from "@cdkglobal/radial";
import {
  SearchBarWrapper,
  SetupsDashBoardContainer,
  SetupsListContainer,
  StyledDepartmentChips,
} from "../../../../utils/styled";
import { useUserContext } from "../../../../contexts/UserContext";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import {
  NavItem,
  SetupListNavInfoByDepartment,
  IOption,
} from "../../../../types";
import { DEFAULT_TOAST } from "../../constants";
import Toaster from "../../../common/Toaster";
import { useIntl } from "react-intl";
import { DepartmentAccordion } from "./DepartmentAccordion";
import { FavoritesSection } from "./FavoritesSection";
import { ampli } from "../../../../ampli";
import { handleSetupSelection } from "../../helpers";

export default function SetupsDashboard(): JSX.Element {
  const intl = useIntl();

  const {
    departments,
    defaultStore,
    setSelected,
    setupListNavInfoByDepartment,
    favorites,
    iamUserState,
    permissions,
    setPermissions,
  } = useUserContext();

  const { networkErrorToast, setLandingPageToast, setNetworkErrorToast } =
    useUIStateContext();

  const [searchValue, setSearchValue] = useState("");
  const departmentOptions = departments.filter(
    (department) =>
      setupListNavInfoByDepartment[department.value]?.setups?.length > 0,
  );
  const [filteredDepartmentOptions, setFilteredDepartmentOptions] = useState<
    string[]
  >([]);
  const [openPanel, setOpenPanel] = useState<Record<string, boolean>>({});

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };
  const handleSetupClick = ({
    setup,
    department,
  }: {
    setup: IOption;
    department: IOption;
  }) => {
    handleSetupSelection({
      setup,
      department,
      store: defaultStore,
      setupListNavInfoByDepartment,
      setLandingPageToast,
      setSelected,
      iamUserState,
      permissions,
      setPermissions,
    });
    rdsSetNavMenuHighlight(setup.value);
  };

  const filterItemsBySearch = (items: NavItem[]): NavItem[] => {
    const searchTerm = searchValue.trim().toLowerCase();

    if (!searchValue.trim()) return items;

    return items.filter(
      (item) =>
        item.text.toLowerCase().includes(searchTerm) ||
        item.id.toLowerCase().includes(searchTerm) ||
        item.description?.toLowerCase().includes(searchTerm),
    );
  };

  const filteredSetupCards = useMemo(() => {
    const result: SetupListNavInfoByDepartment = {};

    Object.keys(setupListNavInfoByDepartment).forEach((key) => {
      const setupsInfo = setupListNavInfoByDepartment[key];
      const items = setupsInfo?.setups || [];

      result[key] = {
        ...setupsInfo,
        setups: filterItemsBySearch(items),
      };
    });

    return result;
  }, [setupListNavInfoByDepartment, searchValue]);

  const filteredFavItems = useMemo(
    () => filterItemsBySearch(favorites) || [],
    [favorites, searchValue],
  );

  const handleFilterDepartmentOptions = (departmentId: string) => {
    setFilteredDepartmentOptions((prev) =>
      prev.includes(departmentId)
        ? prev.filter((id) => id !== departmentId)
        : [...prev, departmentId],
    );
  };

  return (
    <>
      {networkErrorToast.message.length > 0 && (
        <Toaster
          variant={networkErrorToast.variant}
          message={networkErrorToast.message}
          clearMessage={() => setNetworkErrorToast(DEFAULT_TOAST)}
          position="fixed"
          actionText={intl.formatMessage({
            id: "action.dismiss",
            defaultMessage: "Dismiss",
          })}
        />
      )}
      <SetupsDashBoardContainer>
        <SearchBarWrapper>
          <SearchInput
            clearButtonLabel="clearButtonLabel"
            id="search-input-expanded"
            isAlwaysExpanded
            name="search-input-expanded"
            disableBrowserAutocomplete={true}
            onChange={handleChange}
            onClick={() => {
              ampli.searchSetups({
                "Search Bar Clicked": true,
              });
            }}
            placeholder="Search setups"
            size={INPUT_SIZES.STANDARD}
            value={searchValue}
            style={{ maxWidth: "55rem" }}
            dataTestId="dashboard-search-input"
          />
          <TextButton
            onClick={() => {
              setOpenPanel({});
            }}
            text={" Collapse all"}
            dataTestId={`collapse-all-btn`}
          />
        </SearchBarWrapper>

        <SetupsListContainer>
          {departmentOptions?.map((department: IOption) => {
            return (
              <StyledDepartmentChips
                data-testid={`${department.value}-chip`}
                key={department.value}
                label={department.label}
                isSelected={filteredDepartmentOptions.includes(
                  department.value,
                )}
                onClick={() => handleFilterDepartmentOptions(department.value)}
              />
            );
          })}
          <Divider data-testid="divider" showMargins />
          <FavoritesSection
            filteredFavItems={filteredFavItems}
            handleSetupClick={handleSetupClick}
          />
          <Divider data-testid="divider" showMargins />
          <DepartmentAccordion
            filteredDepartmentOptions={filteredDepartmentOptions}
            filteredSetupCards={filteredSetupCards}
            openPanel={openPanel}
            setOpenPanel={setOpenPanel}
            handleSetupClick={handleSetupClick}
            departments={departmentOptions}
          />
        </SetupsListContainer>
      </SetupsDashBoardContainer>
    </>
  );
}
